import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { sendGet, sendPost } from 'hooks/use-fetch'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import { BirthdayToAgeInYearsAndMonths } from 'core/Utilities'
import DateRangeIcon from '@mui/icons-material/DateRange'
import Button from '@mui/material/Button'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import S3Avatar from 'Components/S3Image/S3Avatar'
import Avatar from '@mui/material/Avatar'
import NotesIcon from '@mui/icons-material/Notes'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import { ActionMenu } from 'Components/Menu/ActionMenu'
import { ActionItem } from 'Components/Menu/ActionItem'
import { type SelectOption } from 'Components/SelectWithLabel'
import { toast } from 'react-toastify'

interface Props {
  siblingId: number
  childId: number
}

interface SiblingDetailsModel {
  firstName: string
  lastName: string
  dateOfBirth: Date
  isUnborn: boolean
  address: string
  city: string
  state: string
  zip: string
  race: string
  tribalAffiliationStatus: boolean
  tribalAffiliationName: string | null
  tribalAffiliationID: number | null
  primaryLanguage: string
  s3Key: string | null
  activeCareManagementProvider: string | null
}

const SiblingDetails = (props: Props): JSX.Element => {
  const nav = useNavigate()
  const [sibling, setSibling] = useState<SiblingDetailsModel | null>(null)
  const [showMenu, setShowMenu] = useState(false)
  const [providerList, setProviderList] = useState<Array<SelectOption<number>>>([])
  const [refreshTime, setRefreshTime] = useState(Date.now())

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response, success } = await sendGet(
        `/SiblingDetails/GetDetails?childId=${props.childId}&siblingId=${props.siblingId}`, {})

      success && setSibling(response.response)
    }

    void fetchData()
  }, [props.siblingId, refreshTime])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (sibling !== null && sibling.activeCareManagementProvider == null) {
        const { response: rsp2 } = await sendGet('/CareManagement/IntakeProviders', {})
        setProviderList(rsp2)
      }
    }
    void fetchData()
  }, [sibling])

  const onHide = (): void => {
    setShowMenu(false)
  }
  const onShow = (): void => {
    setShowMenu(true)
  }
  const goToDetails = (): void => {
    nav(`/CareNetworkManagement/Details/${props.siblingId}`)
  }

  const handleIntakeSelect = async (id: number): Promise<void> => {
    const { response: result } = await sendPost('/CareManagement/AssignToIntake', { childId: props.siblingId, assignedOrgID: id })
    if (result.errors.length === 0) {
      setShowMenu(false)
      toast.success(result.message)
      setRefreshTime(Date.now())
      setProviderList([])
    }
  }

  if (sibling == null) return <Skeleton />
  return <Box className='CardDisplay' data-testid='siblingDetailsCard'>
    <Typography variant='h6' sx={{ fontWeight: 'bold', display: 'inline-block' }} gutterBottom>Sibling Details</Typography>
    <Box sx={{ float: 'right' }}>
      <ActionMenu open={showMenu} onClose={onHide} onShow={onShow} testId='siblingMenu' menuProps={{ border: 'none' }}>
        <Box>
          <ActionItem testId='goToDetails' onClick={goToDetails} >Go to Child Details</ActionItem>
          {providerList.length > 0 && <Box sx={{ paddingLeft: 2, fontWeight: 'bold' }}>Assign to Care Management</Box>}
          <Box sx={{ paddingLeft: 2 }}>
            {providerList.map(i => (
              <ActionItem testId={i.name} onClick={async () => { await handleIntakeSelect(i.id) }}>{i.name}</ActionItem>
            ))}
          </Box>
        </Box>
      </ActionMenu>
    </Box>

    <Stack direction='column' spacing={1}>
      <Stack direction='row' spacing={1}>
        {sibling.s3Key !== null
          ? <S3Avatar imageKey={sibling.s3Key} height='30px' width='30px' />
          : <Avatar sx={{ height: '30px', width: '30px' }} />}
        <Typography variant='body1' sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }}>
          {`${sibling.isUnborn ? '(unborn)' : sibling.firstName} ${sibling.lastName}`}
        </Typography>
      </Stack>
      <Stack direction='row' spacing={1}>
        <DateRangeIcon />
        <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.isUnborn
              ? '(unborn)'
              : (sibling?.dateOfBirth !== null && sibling?.dateOfBirth != null && new Date(sibling?.dateOfBirth).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }))}
          </Typography>
          {sibling !== null && !sibling.isUnborn && <Typography variant='body2' sx={{ overflowWrap: 'break-word' }}>
            ({BirthdayToAgeInYearsAndMonths(sibling.dateOfBirth, false)})
          </Typography>}
        </Stack>
      </Stack>
      <Stack direction='row' spacing={1}>
        <PlaceOutlinedIcon />
        <Stack direction='column'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.address}
          </Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {`${sibling.city}, ${sibling.state}`}
          </Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.zip}
          </Typography>
        </Stack>
      </Stack>
      {!sibling.isUnborn && <Stack direction='row' spacing={1}>
        <NotesIcon />
        <Stack direction='column'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.race}
          </Typography>
          {sibling.tribalAffiliationStatus && (
            <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
              {sibling.tribalAffiliationName}
            </Typography>

          )}
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.primaryLanguage}
          </Typography>
        </Stack>
      </Stack>
      }
      {sibling.activeCareManagementProvider !== null &&
        <Stack direction='column'>
          <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Care Management</Typography>
          <Stack direction='row' spacing={1}>
            <AccountBalanceIcon />
            <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
              {sibling.activeCareManagementProvider}
            </Typography>
          </Stack>
        </Stack>
      }
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant='contained'
          onClick={() => {
            nav(`/SiblingDetails/Edit/${props.siblingId}/Child/${props.childId}`)
          }}
          sx={{ textTransform: 'none', px: 4 }}
          data-testid='editSiblingButton'
        >
          Edit Sibling
        </Button>
      </Box>
    </Stack>
  </Box>
}

export default SiblingDetails
